'use client';
import { usesCareIncidentWizard } from '@/lib/incidents/capture-entry';
import { createClient } from '@/lib/supabase/client';
import { readUnifiedCaptureModuleAdmission } from '@/lib/incidents/register-entitlement-evaluator';
import { useQuery } from '@tanstack/react-query';
import { usePathname, useSearchParams } from 'next/navigation';
import { useResolvedAuthUser } from '@/contexts/resolved-auth-user-context';
import { isFeatureEnabled } from '@/lib/supabase/feature-flags';
import {
  UNIFIED_INCIDENT_CAPTURE_FLAG,
  UNIFIED_INCIDENT_CAPTURE_ROLES,
} from '@/lib/incidents/unified-capture';

/** Same tenant flag as the page, deduped in React Query. Gate only this exact
 * capture route; existing source workflows retain their original admission. */
export function useUnifiedIncidentCaptureAdmission(requireModuleAccess = false) {
  const pathname = usePathname();
  const params = useSearchParams();
  const actor = useResolvedAuthUser();
  const candidate =
    pathname === '/compliance/incidents/new' &&
    Boolean(actor?.tenantId) &&
    UNIFIED_INCIDENT_CAPTURE_ROLES.includes(actor?.role ?? '') &&
    !usesCareIncidentWizard(params ?? new URLSearchParams());
  const flag = useQuery({
    queryKey: ['incident-unified-web-flag', actor?.tenantId, actor?.id],
    enabled: candidate,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    queryFn: () =>
      isFeatureEnabled(UNIFIED_INCIDENT_CAPTURE_FLAG, actor?.tenantId).catch(() => false),
  });
  const modules = useQuery({
    queryKey: ['incident-unified-register-admission', actor?.tenantId, actor?.id],
    enabled: candidate && flag.data === true && requireModuleAccess,
    retry: false,
    queryFn: () =>
      actor?.tenantId
        ? readUnifiedCaptureModuleAdmission(createClient(), actor.tenantId)
        : Promise.resolve(false),
  });
  return {
    moduleAllowed: modules.data === true,
    modulePending: requireModuleAccess && candidate && flag.data === true && modules.isPending,
    allowed: candidate && flag.data === true,
    pending: candidate && flag.isPending,
  };
}
